// eslint-disable-next-line import-x/no-extraneous-dependencies
import { Time } from '@internationalized/date';

import { initialParameters } from './config';
import { ScheduleElementValues } from './types';
import { ONE_DAY_IN_SECONDS } from '../../utils/time';

import type { ScheduleElement, ScheduleElementColor, ScheduleElementInput } from '../../graphql/components';

export const hexToRgb: (hex: string) => ScheduleElementColor = (hex) => {
  // Remove the '#' if present
  let cleanValue = hex.replace(/^#/, '');

  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  if (cleanValue.length === 3) {
    cleanValue = cleanValue
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // Parse the r, g, b values
  const bigint = parseInt(cleanValue, 16);

  return {
    // eslint-disable-next-line no-bitwise
    red: (bigint >> 16) & 255,
    // eslint-disable-next-line no-bitwise
    green: (bigint >> 8) & 255,
    // eslint-disable-next-line no-bitwise
    blue: bigint & 255,
  };
};

export const rgbToHex = (color: ScheduleElementColor) => {
  const toHex = (value: number) => {
    const hex = value.toString(16);

    return `${hex.length === 1 ? '0' : ''}${hex}`;
  };

  return `#${toHex(color.red)}${toHex(color.green)}${toHex(color.blue)}`;
};

export const normalizeScheduleElement: (scheduleElement: ScheduleElement) => ScheduleElementValues = (
  scheduleElement
) => {
  const {
    id,
    color,
    defaultDuration,
    name,
    paid,
    paidTimeOff,
    pinnable,
    productive,
    shift,
    showInOverview,
    showInOverviewBig,
    sick,
    vacation,
    tags,
    zoomLevel,
  } = scheduleElement;

  const isWholeDayElement = defaultDuration === ONE_DAY_IN_SECONDS;

  return {
    id,
    isWholeDayElement,
    name,
    zoomLevel,
    color: rgbToHex(color),
    defaultDuration: isWholeDayElement
      ? undefined
      : new Time(Math.floor(defaultDuration / 3600), Math.floor(defaultDuration % 3600) / 60),
    parameters: {
      paid,
      paidTimeOff,
      pinnable,
      productive,
      shift,
      showInOverview,
      showInOverviewBig,
      sick,
      vacation,
    },
    tags: tags.map(({ id: tagId }) => tagId),
  };
};

export const transformFormValuesToScheduleElementObject: (values: ScheduleElementValues) => ScheduleElementInput = (
  values
) => {
  const {
    color,
    defaultDuration,
    id,
    isWholeDayElement,
    name,
    parameters: { paid, paidTimeOff, pinnable, productive, shift, showInOverview, showInOverviewBig, sick, vacation },
    tags,
    zoomLevel,
  } = values;

  return {
    id,
    name,
    paid,
    paidTimeOff,
    pinnable,
    productive,
    shift,
    showInOverview,
    showInOverviewBig,
    sick,
    tags,
    vacation,
    zoomLevel,
    color: hexToRgb(color),
    defaultDuration: isWholeDayElement
      ? ONE_DAY_IN_SECONDS
      : (defaultDuration?.hour ?? 0) * 3600 + (defaultDuration?.minute ?? 0) * 60 + (defaultDuration?.second ?? 0),
  };
};

export const mapKeysToParameters = (keys: Array<keyof ScheduleElementValues['parameters']>) => {
  const parameters = { ...initialParameters };

  keys.forEach((key) => {
    parameters[key] = true;
  });

  return parameters;
};
